import type { RouteRecordRaw } from 'vue-router';

/**
 * ADMIN-T012 — admin route table per SPEC §5.2 / §5.3.
 * Page components are placeholders for M1; real pages land in M3/M4.
 */
const operationalAdminRoutes: RouteRecordRaw[] = [
  {
    path: '/admin/notification-deliveries',
    name: 'admin.notification-deliveries',
    component: () => import('@/modules/admin/notificationDeliveries/NotificationDeliveryPage.vue'),
    meta: { permission: 'notification_delivery:read', data_scope: 'global', lazy_chunk_name: 'admin-notification-deliveries', title: '服务号消息记录' },
  },
  {
    path: '/admin/service-reminders',
    name: 'admin.service-reminders',
    component: () => import('@/modules/admin/serviceReminders/ServiceReminderLedgerPage.vue'),
    meta: { permission: 'service_reminder:read', data_scope: 'global', lazy_chunk_name: 'admin-service-reminder-ledger', title: '提醒单台账' },
  },
  {
    path: '/admin/service-reminders/new',
    name: 'admin.service-reminders.new',
    component: () => import('@/modules/admin/serviceReminders/ServiceReminderEntryPage.vue'),
    meta: { permission: 'service_reminder:write', data_scope: 'global', lazy_chunk_name: 'admin-service-reminders', title: '服务提醒单录入' },
  },
  {
    path: '/admin',
    name: 'admin.home',
    component: () => import('@/modules/admin/home/AdminHomePage.vue'),
    meta: {
      permission: null,
      requiresAuth: true,
      data_scope: 'self',
      lazy_chunk_name: 'admin-home',
      title: '工作台',
    },
  },

  {
    path: '/admin/shipments',
    name: 'admin.shipments',
    // ADMIN-T051 — 水质报告/配件流转台账: 备货→寄出→物流→签收→客户确认全流程追踪 (manual entry, no carrier API).
    component: () => import('@/modules/admin/shipments/ShipmentListPage.vue'),
    meta: {
      permission: 'shipment:read',
      data_scope: 'project',
      lazy_chunk_name: 'admin-shipments',
      title: '报告/配件流转',
    },
  },

  {
    path: '/admin/config/process-template',
    name: 'admin.config.process-template',
    component: () => import('./placeholder.vue'),
    meta: {
      // ADMIN-T358: aligned to core's authority code (the real process/policy config is
      // gated by config:work_order_policy:write; legacy `config:process:write` was not
      // in the matrix). This placeholder route is not in the nav.
      permission: 'config:work_order_policy:write',
      data_scope: 'global',
      lazy_chunk_name: 'admin-config-process-template',
      title: '流程模板',
    },
  },
  {
    path: '/admin/config/aftersales-global-fallback',
    name: 'admin.config.aftersales-global-fallback',
    component: () => import('@/modules/admin/aftersales/GlobalFallbackPage.vue'),
    meta: { permission: 'aftersales_responsibility:read', data_scope: 'global', lazy_chunk_name: 'admin-aftersales-global-fallback', title: '售后全局兜底' },
  },
  {
    path: '/admin/config/aftersales-stall-policy',
    name: 'admin.config.aftersales-stall-policy',
    component: () => import('@/modules/admin/aftersales/StallPolicyPage.vue'),
    meta: { permission: 'config:aftersales_stall:read', data_scope: 'global', lazy_chunk_name: 'admin-aftersales-stall-policy', title: '分级提醒时间' },
  },
  {
    path: '/admin/config/work-order-policy',
    name: 'admin.config.work-order-policy',
    // ADMIN-T310 — D2/D3 工单策略配置：订单来源/流程模板 + 需求类型 + 紧急度 SLA.
    component: () => import('@/modules/admin/config/WorkOrderPolicyPage.vue'),
    meta: {
      permission: 'config:work_order_policy:read',
      data_scope: 'global',
      lazy_chunk_name: 'admin-config-work-order-policy',
      title: '工单策略配置',
    },
  },
  {
    path: '/admin/employees/new',
    name: 'admin.employees.new',
    component: () => import('@/modules/admin/employees/EmployeeEntryPage.vue'),
    meta: { permission: 'employee:write', data_scope: 'project', lazy_chunk_name: 'admin-employees-new', title: '员工录入' },
  },
  {
    path: '/admin/employees',
    name: 'admin.employees',
    component: () => import('@/modules/admin/employees/EmployeeManagementPage.vue'),
    meta: { permission: 'employee:read', data_scope: 'region', lazy_chunk_name: 'admin-employees', title: '员工管理' },
  },

  {
    path: '/admin/alerts/preview',
    name: 'admin.alerts.preview',
    // ADMIN-T201 — 旁线预警发送预览 (simulated, no real push).
    component: () => import('@/modules/admin/alerts/AlertPreviewPage.vue'),
    meta: {
      // ADMIN-T357: core gates GET /admin-bff/alerts with alerts:read.
      permission: 'alerts:read',
      data_scope: 'project',
      lazy_chunk_name: 'admin-alerts-preview',
      title: '预警发送预览',
    },
  },
  {
    path: '/admin/reminders',
    name: 'admin.reminders',
    // ADMIN-T341 — 提醒意图日志：GET /admin-bff/reminders；on-screen surface for all 6 kinds.
    component: () => import('@/modules/admin/reminders/ReminderLogPage.vue'),
    meta: {
      // ADMIN-T357: core gates GET /admin-bff/reminders with reminder:read.
      permission: 'reminder:read',
      data_scope: 'project',
      lazy_chunk_name: 'admin-reminders',
      title: '提醒意图日志',
    },
  },

  {
    path: '/admin/finance',
    name: 'admin.finance',
    // ADMIN-T049 — 财务协同: invoice + payment ledger with mark-complete actions.
    component: () => import('@/modules/admin/finance/FinancePage.vue'),
    meta: {
      permission: 'finance:read',
      data_scope: 'project',
      lazy_chunk_name: 'admin-finance',
      title: '财务协同',
    },
  },
  {
    path: '/admin/weekly',
    name: 'admin.weekly',
    // ADMIN-T330 — C15/D16: weekly report export page. Triggers BI export
    // (dashboard_weekly / anomaly_summary_weekly) as HTML; shows task list
    // + archive download. format='html' posted to POST /bi/v1/exports.
    component: () => import('@/modules/admin/weekly/WeeklyReportPage.vue'),
    meta: {
      permission: 'weekly:read',
      data_scope: 'region',
      lazy_chunk_name: 'admin-weekly',
      title: '周报导出',
    },
  },
  {
    path: '/admin/export-center',
    name: 'admin.export-center',
    // ADMIN-T056 — Export Center task list: running/done/failed/downloaded/expired jobs.
    component: () => import('@/modules/admin/export-center/ExportCenterPage.vue'),
    meta: {
      permission: 'export:read',
      data_scope: 'self',
      lazy_chunk_name: 'admin-export-center',
      title: '导出中心',
    },
  },
  {
    // ADMIN-T389 / CORE-T300p — admin-only deep-link page for person-scoped
    // identity binding detail + revoke + audit. No menu entry; navigated from
    // coordinated admin flows once a personId seam exists.
    path: '/admin/identity/persons/:personId',
    name: 'admin.identity.person',
    component: () => import('@/modules/admin/identity/PersonIdentityDetailPage.vue'),
    meta: {
      permission: 'identity:write',
      data_scope: 'global',
      lazy_chunk_name: 'admin-identity-person',
      title: '身份绑定详情',
    },
  },
  {
    // ADMIN-T359 / CORE-T262 — 平台用户管理: local admin account CRUD (admin-only). The route
    // needs user:read (admin-only in config/roles.yaml); write actions inside are gated by
    // <Can user:write>. Core enforces the safety guards (cannot disable self / last admin).
    path: '/admin/user',
    name: 'admin.user',
    component: () => import('@/modules/admin/users/UserManagementPage.vue'),
    meta: {
      permission: 'user:read',
      data_scope: 'global',
      lazy_chunk_name: 'admin-user',
      title: '平台用户管理',
    },
  },
  {
    // ADMIN-T360 / CORE-T263 — 角色管理: read/edit the local role matrix (admin-only). The
    // route needs role:read; the save action needs role:write (wrapped in <Can>). The
    // built-in admin role is protected; management codes (user:*/role:*) are admin-only.
    path: '/admin/role',
    name: 'admin.role',
    component: () => import('@/modules/admin/roles/RoleManagementPage.vue'),
    meta: {
      permission: 'role:read',
      data_scope: 'global',
      lazy_chunk_name: 'admin-role',
      title: '角色管理',
    },
  },
  {
    path: '/admin/audit',
    name: 'admin.audit',
    component: () => import('@/modules/admin/audit/AuditLogPage.vue'),
    meta: {
      permission: 'audit:read',
      data_scope: 'global',
      lazy_chunk_name: 'admin-audit',
      title: '审计日志',
    },
  },
  {
    path: '/admin/config/notification-templates',
    name: 'admin.config.notification-templates',
    component: () => import('@/modules/admin/template/notification/NotificationTemplatePage.vue'),
    meta: {
      permission: 'config:notification_template:read',
      data_scope: 'global',
      lazy_chunk_name: 'admin-config-notif-tpl',
      title: '通知模板配置',
    },
  },
  {
    // ADMIN-T352 / CORE-T256 — D17/D18: export template configuration. Now local-real:
    // core implements GET/PUT /admin-bff/config/export-templates (ExportTemplateService).
    // (ADMIN-T379 corrected the stale "MSW-only seam" note.)
    path: '/admin/config/export-templates',
    name: 'admin.config.export-templates',
    component: () => import('@/modules/admin/config/ExportTemplateConfigPage.vue'),
    meta: {
      permission: 'config:export_template:read',
      data_scope: 'global',
      lazy_chunk_name: 'admin-config-export-tpl',
      title: '导出模板配置',
    },
  },
  {
    // ADMIN-T380/T382 — Party-A integration status / mapping view (local-real, backend core).
    // admin-only via integration:read (held through the admin `*`). Backend-backed via core's
    // admin-BFF → S3 integration (CORE-T281 / INTEG-T817): demo is MSW (session-only); dev:real
    // reads/mutates integration's real writeback ledger. Real Party-A network/credentials/staging
    // remain external-later (INTEG-T816; default fake provider does not contact real Party-A).
    path: '/admin/integration/party-a',
    name: 'admin.integration.party-a',
    component: () => import('@/modules/admin/integration/PartyAStatusPage.vue'),
    meta: {
      permission: 'integration:read',
      data_scope: 'global',
      lazy_chunk_name: 'admin-integration-party-a',
      title: '甲方接口状态',
    },
  },
  {
    path: '/admin/presales-cost-rates',
    name: 'admin.presales.cost-rates',
    component: () => import('@/modules/admin/presales/PresalesCostRatePage.vue'),
    meta: { permission: 'presales_cost:read', data_scope: 'global', lazy_chunk_name: 'admin-presales-cost-rates', title: '售前人工成本参数' },
  },
  {
    path: '/admin/presales-assignment-rules',
    name: 'admin.presales.assignment-rules',
    component: () => import('@/modules/admin/presales/PresalesAssignmentRulePage.vue'),
    meta: { permission: 'presales_assignment_rule:read', data_scope: 'global', lazy_chunk_name: 'admin-presales-assignment-rules', title: '售前自动分派规则' },
  },
  {
    path: '/admin/presales-production-notices',
    name: 'admin.presales.notice.list',
    component: () => import('@/modules/admin/presales/ProductionNoticeListPage.vue'),
    meta: { permission: 'presales_notice:read', data_scope: 'global', lazy_chunk_name: 'admin-presales-notices', title: '售前生产通知单' },
  },
  {
    path: '/admin/presales-production-notices/new',
    name: 'admin.presales.notice.new',
    component: () => import('@/modules/admin/presales/ProductionNoticeEntryPage.vue'),
    meta: { permission: 'presales_notice:write', data_scope: 'global', lazy_chunk_name: 'admin-presales-notice-new', title: '新建生产通知单' },
  },
  {
    path: '/admin/presales-production-notices/:id/edit',
    name: 'admin.presales.notice.edit',
    component: () => import('@/modules/admin/presales/ProductionNoticeEntryPage.vue'),
    meta: { permission: 'presales_notice:write', data_scope: 'global', lazy_chunk_name: 'admin-presales-notice-edit', title: '编辑生产通知单草稿' },
  },
  {
    path: '/admin/presales-production-notices/:id',
    name: 'admin.presales.notice.detail',
    component: () => import('@/modules/admin/presales/ProductionNoticeDetailPage.vue'),
    meta: { permission: 'presales_notice:read', data_scope: 'global', lazy_chunk_name: 'admin-presales-notice-detail', title: '生产通知单详情' },
  },
];
const masterDataRoutes: RouteRecordRaw[] = [
  {
    path: '/admin/master-customers',
    name: 'admin.master-customers',
    component: () => import('@/modules/admin/masterData/MasterDataLedgerPage.vue'),
    meta: { permission: 'master_customer:read', data_scope: 'project', masterDomain: 'customer', title: '客户主数据' },
  },
  {
    path: '/admin/master-customers/:id',
    name: 'admin.master-customers.detail',
    component: () => import('@/modules/admin/masterData/MasterDataDetailPage.vue'),
    meta: { permission: 'master_customer:read', data_scope: 'project', masterDomain: 'customer', title: '客户主数据详情' },
  },
  {
    path: '/admin/master-devices',
    name: 'admin.master-devices',
    component: () => import('@/modules/admin/masterData/MasterDataLedgerPage.vue'),
    meta: { permission: 'master_device:read', data_scope: 'project', masterDomain: 'device', title: '设备主数据' },
  },
  {
    path: '/admin/master-devices/:id',
    name: 'admin.master-devices.detail',
    component: () => import('@/modules/admin/masterData/MasterDataDetailPage.vue'),
    meta: { permission: 'master_device:read', data_scope: 'project', masterDomain: 'device', title: '设备主数据详情' },
  },
  {
    path: '/admin/work-order-master',
    name: 'admin.work-order-master',
    component: () => import('@/modules/admin/workOrderMaster/WorkOrderMasterPage.vue'),
    meta: { permission: 'work_order_master:read', data_scope: 'global', title: '工单主数据' },
  },
  {
    path: '/admin/project-contracts',
    name: 'admin.project-contracts',
    component: () => import('@/modules/admin/masterData/MasterDataLedgerPage.vue'),
    meta: { permission: 'project_contract:read', data_scope: 'project', masterDomain: 'project_contract', title: '项目合同' },
  },
  {
    path: '/admin/project-contracts/:id',
    name: 'admin.project-contracts.detail',
    component: () => import('@/modules/admin/masterData/MasterDataDetailPage.vue'),
    meta: { permission: 'project_contract:read', data_scope: 'project', masterDomain: 'project_contract', title: '项目合同详情' },
  },
  {
    path: '/admin/project-contracts/:id/debug',
    name: 'admin.project-contracts.debug',
    component: () => import('@/modules/admin/masterData/MasterDataDebugPage.vue'),
    meta: { permission: 'project_contract:read', data_scope: 'project', masterDomain: 'project_contract', title: '项目合同接口调试' },
  },
  {
    path: '/admin/maintenance-projects',
    name: 'admin.maintenance-projects',
    component: () => import('@/modules/admin/masterData/MasterDataLedgerPage.vue'),
    meta: { permission: 'maintenance_project:read', data_scope: 'project', masterDomain: 'maintenance_project', title: '维保项目' },
  },
  {
    path: '/admin/maintenance-projects/:id',
    name: 'admin.maintenance-projects.detail',
    component: () => import('@/modules/admin/masterData/MasterDataDetailPage.vue'),
    meta: { permission: 'maintenance_project:read', data_scope: 'project', masterDomain: 'maintenance_project', title: '维保项目详情' },
  },
  {
    path: '/admin/master-data/sync-jobs',
    name: 'admin.master-data.sync-jobs',
    component: () => import('@/modules/admin/masterData/MasterDataSyncJobsPage.vue'),
    meta: { permission: 'master_data:sync:read', data_scope: 'global', title: '主数据同步' },
  },
  {
    path: '/admin/master-data/rejections',
    name: 'admin.master-data.rejections',
    component: () => import('@/modules/admin/masterData/MasterDataRejectionsPage.vue'),
    meta: { permission: 'master_data:sync:read', data_scope: 'global', title: '同步隔离记录' },
  },
];

export const adminRoutes: RouteRecordRaw[] = [
  ...masterDataRoutes,
  ...operationalAdminRoutes,
];
